// @flow

import React from 'react';
import { withTranslation } from 'react-i18next';

import ApplyPage from './apply-page';
import { logGAEvent } from '../util/log-ga';
import LoadingMessage from './loading-message';
import NotFoundPage from './not-found';
import { ApiClient } from '../api/apply-page-api-client';
import { ApplyContext } from '../apply-context';
import ReferralApply from './apply-pages/referral-apply';
import i18n from '../i18n';

type Props = {
  sid: ?string,
  source: ?string,
  version: ?string,
  jobHash: string,
  referralHash: ?string,
  t: (value: string, opts?: any) => string,
}

type State = {
  job: ?Job,
  jobError: ?string,
  jobLoaded: boolean,
  visitId: ?number,
}

class ApplyPageContainer extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      job: null,
      jobError: null,
      jobLoaded: false,
      visitId: null,
    };
  }

  componentDidMount = async () => {
    logGAEvent('Application', 'Apply');
    const response = await ApiClient.getJob(this.props.jobHash)
    if (!response.ok) {
      this.setState({
        jobError: this.props.t('missingJob'),
      });
      return;
    }
    const result = await response.json();
    i18n.changeLanguage(result.job.language || 'en');
    this.setState({
      jobLoaded: true,
      job: result.job,
      jobError: null,
    });

    if (this.props.referralHash) {
      /*
      We should probably make version nullable in application_visits. Right
      now it's not and in the past, we would randomly select an apply page
      that was never used for referral applications, but was logged in the DB.
       */
      this.logApplicationVisit('S');
    }
  }

  logApplicationVisit = async (version: string) => {
    const {
      visit_id,
    } = await ApiClient.logApplicationVisit(this.props.jobHash, version, this.props.source, this.props.sid);
    this.setState({
      visitId: visit_id,
    });
  };

  render() {
    const {
      job,
      jobError,
    } = this.state;
    const {
      referralHash,
      version,
    } = this.props;
    if (jobError) {
      return <NotFoundPage errorMessage={jobError} />;
    }
    if (!job) {
      return (
        <LoadingMessage />
      );
    }

    const context = {
      source: this.props.source,
      sourceId: this.props.sid,
      jobHash: this.props.jobHash,
      referralHash: this.props.referralHash,
      visitId: this.state.visitId,
    };

    return (
      <ApplyContext.Provider value={context}>
        {referralHash ? (
          <ReferralApply
            job={job}
            org={job.org}
            referralHash={referralHash}
          />
          ) : (
          <ApplyPage
            job={job}
            org={job.org}
            logApplicationVisit={this.logApplicationVisit}
            version={version}
          />
        )}
      </ApplyContext.Provider>
    );
  }
}

export default withTranslation()(ApplyPageContainer);
